import { INFO_LINES, } from "./receipt-template.js";
/**
 * A ordem da loja, completada com o que ela ainda não conhecia.
 *
 * Um modelo gravado antes de uma linha nova existir não a traz em `infoOrder`.
 * Em vez de sumir com ela, a linha entra no fim, na posição de fábrica — senão
 * acrescentar um dado ao papel exigiria que cada loja abrisse o editor.
 * Repetidas e desconhecidas caem fora: o modelo é dado vindo do banco.
 */
export const effectiveOrder = (order) => {
    const seen = new Set();
    const known = new Set(INFO_LINES);
    const result = [];
    for (const line of order) {
        if (!known.has(line) || seen.has(line))
            continue;
        seen.add(line);
        result.push(line);
    }
    for (const line of INFO_LINES) {
        if (!seen.has(line))
            result.push(line);
    }
    return result;
};
/** Os blocos de dados, na ordem que a loja escolheu. */
export const infoBlocks = (template, facts) => {
    const blocks = [];
    for (const line of effectiveOrder(template.infoOrder)) {
        const block = facts[line];
        if (block === undefined || block === null)
            continue;
        // Uma linha do editor pode ser várias no papel: o endereço campo a campo,
        // o telefone e o celular (F161). Elas andam juntas.
        if (Array.isArray(block))
            blocks.push(...block);
        else
            blocks.push(block);
    }
    return blocks;
};
/**
 * As linhas do cliente, obedecendo às chaves do editor.
 *
 * Uma função só para o recibo e o orçamento: as duas decidiam sozinhas o que
 * imprimir do cliente, e o dia em que uma aprendesse o CPF e a outra não, o
 * mesmo cliente sairia de um jeito no orçamento e de outro na venda.
 */
export const customerFacts = (template, customer, 
/** O nome, para a linha "Cliente" ganhar o código na frente (F161). */
customerName) => {
    const pair = (label, value) => value === null || value.trim() === ''
        ? null
        : { kind: 'pair', label, value };
    const filled = (value) => value !== null && value !== undefined && value.trim() !== '';
    if (customer === undefined)
        return {};
    /*
     * O telefone: cada um com o seu nome quando vieram todos, ou o preferido
     * como "WhatsApp" (F160). O mesmo número em dois cadastros sai uma vez.
     */
    const phones = () => {
        if (customer.phones === undefined || customer.phones.length === 0) {
            return pair('WhatsApp', customer.phone);
        }
        const seen = new Set();
        const blocks = [];
        for (const entry of customer.phones) {
            if (!filled(entry.value) || seen.has(entry.value))
                continue;
            seen.add(entry.value);
            blocks.push({ kind: 'pair', label: entry.label, value: entry.value });
        }
        return blocks.length === 0 ? null : blocks;
    };
    /*
     * O endereço é um TEXTO, e não um par: ele não cabe à direita de um rótulo
     * numa bobina de 58 mm. "Rua Sete de Setembro, 1234 — Centro" num par
     * perderia o rótulo inteiro e ainda quebraria mal. Campo a campo (F161),
     * cada parte é curta o bastante para ser um par, como no papel antigo.
     */
    const address = () => {
        const parts = customer.addressParts;
        if (template.customer.addressStyle === 'fields' && parts !== undefined) {
            const rows = [
                ['Endereço', parts.street],
                ['Número', parts.number],
                ['Bairro', parts.district],
                ['Complemento', parts.complement],
                ['CEP', parts.postalCode],
                ['Cidade', parts.cityState],
            ];
            const blocks = rows.flatMap(([label, value]) => filled(value) ? [{ kind: 'pair', label, value }] : []);
            if (blocks.length > 0)
                return blocks;
        }
        return customer.address !== null && customer.address.trim() !== ''
            ? { kind: 'text', text: `Endereço: ${customer.address}` }
            : null;
    };
    return {
        ...(template.customer.showCode &&
            filled(customer.code) &&
            customerName !== undefined
            ? {
                /*
                 * Texto, e não par — F211. Com o nome fantasia E a razão social
                 * ("123 - TOPTECH — RACLAN VIEIRA RAMOS (TOP TECH)") o par não
                 * cabia: o valor nunca quebra, então o rótulo sumia e o fim do nome
                 * era cortado pelo papel. O texto quebra na linha de baixo.
                 */
                customer: {
                    kind: 'text',
                    text: `Cliente: ${customer.code} - ${customerName}`,
                },
            }
            : {}),
        customerDocument: template.customer.showDocument
            ? pair('CPF/CNPJ', customer.document)
            : null,
        customerPhone: template.customer.showPhone ? phones() : null,
        customerAddress: template.customer.showAddress ? address() : null,
        customerEmail: template.customer.showEmail
            ? pair('E-mail', customer.email)
            : null,
    };
};
//# sourceMappingURL=info-lines.js.map